/**
 * Regjistro / redakto aset — `/assets/new` dhe `/assets/:id/edit`.
 *
 * Faqe e plotë, jo dialog: një aset ka katër blloqe të dhënash (identiteti,
 * financat, specifikimet, aksesorët) plus fotot dhe caktimin e parë. Në një
 * modal ato do të bëheshin një kolonë e gjatë me skroll brenda skrolli.
 *
 * Tri vendime që duken te ekrani:
 *
 *  · **Kodi gjenerohet, nuk shkruhet** — dy përdorues që shtojnë njëkohësisht do
 *    të zgjidhnin të njëjtin numër.
 *  · **Specifikimet varen nga kategoria** — një automjet pyetet për targat dhe
 *    kilometrazhin, jo për RAM. Ndryshimi i kategorisë ndryshon fushat.
 *  · **Caktimi i parë është opsional dhe i veçantë** — dorëzimi është akt, ndaj
 *    kryhet pas ruajtjes, me të njëjtën rrugë si çdo dorëzim tjetër. Ashtu
 *    historiku e mban si dorëzim, jo si "u krijua kështu".
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box, Paper, Typography, Stack, Button, TextField, MenuItem, Grid, Alert,
  IconButton, Table, TableHead, TableBody, TableRow, TableCell, Divider,
  Switch, Autocomplete, CircularProgress, InputAdornment, Tooltip,
} from '@mui/material';
import ArrowBackIcon    from '@mui/icons-material/ArrowBack';
import SaveIcon         from '@mui/icons-material/Save';
import AddIcon          from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileIcon   from '@mui/icons-material/UploadFile';
import Inventory2Icon   from '@mui/icons-material/Inventory2';
import MemoryIcon       from '@mui/icons-material/Memory';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';

import { ReportPage } from '../report/ReportKit';
import { FORM_DENSITY, Section, ReadOut, SHRINK } from './FormKit';
import Toast          from '../actions/Toasts';
import assetsService  from '../../services/assetsService';
import { shrinkImage } from '../../utils/imagePrep';
import apiService     from '../../services/apiServices';
import normalizeWorker from '../../services/workerNormalizer';
import AuthImage      from './AuthImage';
import WorkerPicker   from '../common/WorkerPicker';
import {
  CATEGORIES, CONDITIONS, STATUSES, PAYMENT_METHODS, specFields,
  identifierLabel, money, residualValue,
  SPEC_SUGGESTIONS, specRows, specsFromRows, rowUid,
} from '../../config/assets';

const todayISO = () => new Date().toISOString().slice(0, 10);

const EMPTY = {
  name: '', category: 'laptop', subcategory: '', brand: '', model: '',
  serialNumber: '', internalCode: '', purchaseDate: '', purchaseValue: '',
  currency: 'EUR', supplier: '', invoiceNo: '', warrantyUntil: '', location: '',
  condition: 'good', status: 'available', paymentMethod: '', amortizationYears: '',
  departmentId: '', responsibleWorkerId: '', description: '', notes: '',
};

const emptyAccessory = () => ({ uid: rowUid(), name: '', description: '', serialNumber: '', condition: 'good', included: true });

export default function AssetFormPage() {
  const { id }  = useParams();
  const history = useHistory();
  const editing = !!id;

  const [form, setForm]     = useState(EMPTY);
  const [specs, setSpecs]   = useState(() => specRows({}, 'laptop'));
  const [accessories, setAccessories] = useState([]);
  const [photos, setPhotos] = useState([]);
  // Fotot e zgjedhura para se aseti të ekzistojë. Skedari i takon një aseti,
  // ndaj ngarkimi bëhet pas krijimit — por përdoruesi nuk ka pse ta dijë këtë
  // renditje: ai i zgjedh tani dhe i sheh tani.
  const [pending, setPending] = useState([]);
  const [asset, setAsset]   = useState(null);

  const [workers, setWorkers]         = useState([]);
  const [departments, setDepartments] = useState([]);

  const [loading, setLoading] = useState(editing);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [snack, setSnack]     = useState({ open: false, msg: '', sev: 'success' });
  const say = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  // Caktimi i parë — vetëm te regjistrimi i ri.
  const [assignOn, setAssignOn]     = useState(false);
  const [firstWorker, setFirstWorker] = useState('');
  const [firstAssign, setFirstAssign] = useState({ assignedOn: todayISO(), condition: 'good', accessories: '', note: '' });

  const load = useCallback(async () => {
    if (!editing) return;
    setLoading(true);
    try {
      const d = await assetsService.detail(id);
      setAsset(d.asset);
      setForm({
        ...EMPTY,
        ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, d.asset[k] ?? EMPTY[k]])),
        purchaseValue:     d.asset.purchaseValue ?? '',
        amortizationYears: d.asset.amortizationYears ?? '',
        departmentId:      d.asset.departmentId ?? '',
        responsibleWorkerId: d.asset.responsibleWorkerId ?? '',
      });
      setSpecs(specRows(d.asset.specs || {}, d.asset.category));
      setAccessories((d.accessories || []).map((a) => ({ ...a, uid: rowUid() })));
      setPhotos((d.files || []).filter((f) => f.kind === 'photo'));
    } catch (e) {
      setError(e.message || 'Aseti nuk u ngarkua.');
    } finally {
      setLoading(false);
    }
  }, [editing, id]);

  useEffect(() => { load(); }, [load]);

  // Object-URL-të e parapamjes jetojnë sa faqja. Pastrimi lexon një ref, jo
  // gjendjen: një `useEffect` me varësi bosh do ta mbyllte brenda vetes listën
  // e renderimit të parë — pra bosh — dhe s'do të lironte asgjë.
  const pendingRef = useRef([]);
  useEffect(() => { pendingRef.current = pending; }, [pending]);
  useEffect(() => () => pendingRef.current.forEach((x) => URL.revokeObjectURL(x.url)), []);

  useEffect(() => {
    let alive = true;
    Promise.all([apiService().get('workers'), apiService().get('departments')])
      .then(([w, d]) => {
        if (!alive) return;
        const arr = (r) => { const raw = r?.data || r; return Array.isArray(raw) ? raw : []; };
        setWorkers(arr(w).map((x, i) => normalizeWorker(x, i)));
        setDepartments(arr(d));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Ndryshimi i kategorisë SHTON sugjerimet e reja, nuk i fshin rreshtat
  // ekzistues: dikush që shkroi "Numri i shasisë" te "Tjetër" dhe pastaj zgjodhi
  // "Automjet" nuk guxon ta humbasë atë që shkroi.
  useEffect(() => {
    setSpecs((rows) => {
      const have = new Set(rows.map((r) => r.key));
      const add = specFields(form.category)
        .filter(([k]) => !have.has(k))
        .map(([key, label]) => ({ uid: rowUid(), key, label, value: '' }));
      return add.length ? [...rows, ...add] : rows;
    });
  }, [form.category]);
  const preview = useMemo(() => residualValue({
    purchaseValue: form.purchaseValue, purchaseDate: form.purchaseDate,
    amortizationYears: form.amortizationYears,
  }, todayISO()), [form.purchaseValue, form.purchaseDate, form.amortizationYears]);

  // Statusi i një pajisjeje në duar të dikujt vendoset nga kthimi, jo nga këtu.
  const statusLocked = editing && asset?.status === 'assigned';

  function updateSpec(i, field, value) {
    // `key` NUK preket: ai është identiteti me të cilin erdhi rreshti dhe hyn te
    // çelësi i React-it. Çelësi i ruajtjes rillogaritet gjithsesi nga etiketa
    // (`specsFromRows` → `specKey`), ndaj «CPU» i shkruar me dorë bie te i njëjti
    // vend si sugjerimi.
    setSpecs((rows) => rows.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));
  }

  function updateAccessory(i, key, value) {
    setAccessories((rows) => rows.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));
  }

  async function uploadPhoto(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!editing) {
      setPending((p) => [...p, { file, url: URL.createObjectURL(file) }]);
      return;
    }
    try {
      // E njëjta rregull si te importi: fotoja zvogëlohet para se të niset.
      const f = await assetsService.uploadFile(id, await shrinkImage(file), { kind: 'photo' });
      setPhotos((p) => [...p, f]);
      say('Fotoja u ngarkua.');
    } catch (err) {
      say(err.message || 'Ngarkimi dështoi.', 'error');
    }
  }

  function removePending(i) {
    setPending((p) => {
      const gone = p[i];
      if (gone) URL.revokeObjectURL(gone.url);   // pa këtë, çdo heqje lë një blob
      return p.filter((_, idx) => idx !== i);
    });
  }

  async function removePhoto(f) {
    try {
      await assetsService.deleteFile(f.id);
      setPhotos((p) => p.filter((x) => x.id !== f.id));
    } catch (err) { say(err.message || 'Fshirja dështoi.', 'error'); }
  }

  async function save() {
    if (!form.name.trim()) { setError('Emri i asetit është i detyrueshëm.'); return; }
    setSaving(true); setError('');
    try {
      const body = {
        ...form,
        purchaseValue:       form.purchaseValue === '' ? null : Number(form.purchaseValue),
        amortizationYears:   form.amortizationYears === '' ? null : Number(form.amortizationYears),
        departmentId:        form.departmentId || null,
        responsibleWorkerId: form.responsibleWorkerId || null,
        specs: specsFromRows(specs),
        accessories,
      };
      if (statusLocked) delete body.status;

      const saved = editing
        ? await assetsService.update(id, body)
        : await assetsService.create(body);

      // Fotot e zgjedhura para ruajtjes ngarkohen tani, kur aseti ka id.
      // Një dështim këtu nuk e rrëzon krijimin: aseti ekziston, fotoja jo —
      // dhe kjo thuhet, në vend që të humbë në heshtje.
      if (!editing && pending.length) {
        const failed = [];
        for (const { file } of pending) {
          try { await assetsService.uploadFile(saved.id, await shrinkImage(file), { kind: 'photo' }); }
          catch { failed.push(file.name); }
        }
        if (failed.length) say(`Aseti u ruajt, por këto foto nuk u ngarkuan: ${failed.join(', ')}`, 'warning');
      }

      // Dorëzimi kryhet si akt më vete, me të njëjtën rrugë si çdo dorëzim
      // tjetër — ashtu historiku e mban si "Dorëzuar", jo si gjendje fillestare.
      if (!editing && assignOn && firstWorker) {
        try {
          await assetsService.assign(saved.id, { ...firstAssign, workerId: firstWorker });
        } catch (err) {
          say(`Aseti u ruajt (${saved.assetCode}), por dorëzimi dështoi: ${err.message}`, 'warning');
          history.push(`/assets/${saved.id}`);
          return;
        }
      }
      history.push(`/assets/${saved.id}`);
    } catch (e) {
      setError(e.message || 'Ruajtja dështoi.');
    } finally {
      setSaving(false);
    }
  }


  if (loading) {
    return <ReportPage><Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box></ReportPage>;
  }

  const specCount = specs.filter((r) => r.value).length;

  return (
    <ReportPage sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, ...FORM_DENSITY }}>

      {/* Koka rri ngjitur lart: te një formular i gjatë, "Ruaj" nuk guxon të jetë
          një ekran poshtë asaj që sapo shkrove. */}
      <Paper variant="outlined" sx={{
        position: 'sticky', top: 0, zIndex: 3, borderRadius: 2, px: 1.5, py: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 1, bgcolor: 'background.paper',
      }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton size="small" onClick={() => history.goBack()} aria-label="Kthehu">
            <ArrowBackIcon sx={{ fontSize: 19 }} />
          </IconButton>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', lineHeight: 1.25 }}>
              {editing ? 'Redakto asetin' : 'Regjistro aset të ri'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {editing ? asset?.assetCode : 'Shto një aset të ri në inventarin e kompanisë'}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" onClick={() => history.goBack()} disabled={saving}
            sx={{ textTransform: 'none' }}>
            Anulo
          </Button>
          <Button size="small" variant="contained" onClick={save} disabled={saving || !form.name.trim()}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 17 }} />}
            sx={{ textTransform: 'none', fontWeight: 700 }}>
            Ruaj asetin
          </Button>
        </Stack>
      </Paper>

      {!!error && <Alert severity="error">{error}</Alert>}

      <Grid container spacing={1.25} alignItems="flex-start">
        <Grid item xs={12} lg={8}>
          <Stack spacing={1.25}>

            {/* ── Identiteti ── */}
            <Section title="Informacioni i asetit"
              icon={<Inventory2Icon sx={{ fontSize: 16, color: 'primary.main' }} />}>
              <Grid container spacing={1.25}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField label="Kodi i asetit" fullWidth size="small" disabled {...SHRINK}
                    value={editing ? asset?.assetCode || '' : ''}
                    placeholder="Gjenerohet automatikisht" />
                </Grid>
                <Grid item xs={12} sm={6} md={5}>
                  <TextField label="Emri i asetit" required fullWidth size="small" autoFocus {...SHRINK}
                    value={form.name} onChange={set('name')} placeholder="P.sh. MacBook Pro 16”" />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField select label="Kategoria" fullWidth size="small" {...SHRINK}
                    value={form.category} onChange={set('category')}>
                    {CATEGORIES.map((c) => <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField label="Nënkategoria" fullWidth size="small" {...SHRINK}
                    value={form.subcategory} onChange={set('subcategory')} placeholder="Opsionale" />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField label="Marka / Prodhuesi" fullWidth size="small" {...SHRINK}
                    value={form.brand} onChange={set('brand')} placeholder="P.sh. Apple" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField label="Modeli" fullWidth size="small" {...SHRINK}
                    value={form.model} onChange={set('model')} placeholder="P.sh. M3 Pro" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  {/* Etiketa ndjek kategorinë (Serial / IMEI / VIN); kolona mbetet
                      një, ndaj kërkimi i gjen të gjitha me një fushë. */}
                  <TextField label={identifierLabel(form.category)} fullWidth size="small" {...SHRINK}
                    value={form.serialNumber} onChange={set('serialNumber')}
                    placeholder="Nuk përsëritet" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField label="Kodi i brendshëm" fullWidth size="small" {...SHRINK}
                    value={form.internalCode} onChange={set('internalCode')}
                    placeholder="P.sh. IT-2024-001" />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField label="Lokacioni" fullWidth size="small" {...SHRINK}
                    value={form.location} onChange={set('location')} placeholder="Zyra qendrore, Depo…" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField select label="Departamenti" fullWidth size="small" {...SHRINK}
                    value={form.departmentId} onChange={set('departmentId')}>
                    <MenuItem value="">—</MenuItem>
                    {departments.map((d) => (
                      <MenuItem key={d.Id ?? d.id} value={d.Id ?? d.id}>{d.Name || d.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <WorkerPicker workers={workers} value={form.responsibleWorkerId}
                    onChange={(v) => setForm((f) => ({ ...f, responsibleWorkerId: v }))}
                    label="Përgjegjësi i asetit" emptyLabel="Pa përgjegjës" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField select label="Gjendja" fullWidth size="small" {...SHRINK}
                    value={form.condition} onChange={set('condition')}>
                    {CONDITIONS.map((c) => <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>)}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  {statusLocked ? (
                    <Tooltip title="Statusi ndryshon vetëm nga kthimi derisa pajisja të jetë te dikush" arrow>
                      <TextField label="Statusi" fullWidth size="small" disabled {...SHRINK} value="E caktuar" />
                    </Tooltip>
                  ) : (
                    <TextField select label="Statusi" fullWidth size="small" {...SHRINK}
                      value={form.status} onChange={set('status')}>
                      {STATUSES.filter((st) => st.key !== 'assigned').map((st) => (
                        <MenuItem key={st.key} value={st.key}>{st.label}</MenuItem>
                      ))}
                    </TextField>
                  )}
                </Grid>
                <Grid item xs={12} md={9}>
                  <TextField label="Përshkrimi / koment" fullWidth size="small" multiline minRows={2}
                    {...SHRINK} value={form.description} onChange={set('description')}
                    placeholder="Shto përshkrim apo komente shtesë…" />
                </Grid>
              </Grid>
            </Section>

            {/* ── Financat ── */}
            <Section title="Të dhëna financiare"
              icon={<PaymentsOutlinedIcon sx={{ fontSize: 16, color: 'primary.main' }} />}>
              <Grid container spacing={1.25}>
                <Grid item xs={6} sm={4} md={2}>
                  <TextField type="date" label="Data e blerjes" fullWidth size="small" {...SHRINK}
                    value={form.purchaseDate} onChange={set('purchaseDate')} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <TextField type="number" label="Vlera e blerjes" fullWidth size="small" {...SHRINK}
                    value={form.purchaseValue} onChange={set('purchaseValue')} placeholder="0.00"
                    InputProps={{ endAdornment: (
                      <InputAdornment position="end" sx={{ '& p': { fontSize: '0.72rem' } }}>
                        {form.currency}
                      </InputAdornment>
                    ) }} />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField label="Furnitori" fullWidth size="small" {...SHRINK}
                    value={form.supplier} onChange={set('supplier')} placeholder="P.sh. iSTYLE" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField label="Fatura / Nr. dokumentit" fullWidth size="small" {...SHRINK}
                    value={form.invoiceNo} onChange={set('invoiceNo')} placeholder="P.sh. INV-2024-0312" />
                </Grid>
                <Grid item xs={6} sm={6} md={2}>
                  <TextField type="date" label="Garancioni deri më" fullWidth size="small" {...SHRINK}
                    value={form.warrantyUntil} onChange={set('warrantyUntil')} />
                </Grid>

                <Grid item xs={6} sm={4} md={3}>
                  <TextField select label="Metoda e pagesës" fullWidth size="small" {...SHRINK}
                    value={form.paymentMethod} onChange={set('paymentMethod')}>
                    <MenuItem value="">—</MenuItem>
                    {PAYMENT_METHODS.map((m) => <MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <TextField type="number" label="Amortizimi (vjet)" fullWidth size="small" {...SHRINK}
                    value={form.amortizationYears} onChange={set('amortizationYears')} placeholder="P.sh. 3" />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  {/* Shifër e llogaritur, jo fushë e çaktivizuar që fton në shkrim. */}
                  <ReadOut label="Vlera e mbetur"
                    value={preview === null ? '—' : money(preview, form.currency)}
                    hint="Nga amortizimi linear" />
                </Grid>
              </Grid>
            </Section>

            {/* ── Specifikimet ── */}
            <Section title={`Specifikimet teknike${specCount ? ` · ${specCount}` : ''}`}
              icon={<MemoryIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
              action={
                <Button size="small" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                  onClick={() => setSpecs((r) => [...r, { uid: rowUid(), key: '', label: '', value: '' }])}>
                  Shto specifikim
                </Button>
              }>
              {/* Listë e hapur, si aksesorët: kategoria sjell sugjerimet e veta —
                  një laptop nis me CPU/RAM/SSD — por asgjë nuk e ndal shkrimin e
                  një fushe të re. Fushat e njohura mbajnë çelës kanonik, ndaj
                  mbeten të krahasueshme mes aseteve. */}
              <Table size="small">
                <TableBody>
                  {specs.map((r, i) => (
                    <TableRow key={r.uid}>
                      <TableCell sx={{ width: '38%', pl: 0 }}>
                        <Autocomplete
                          freeSolo size="small" options={SPEC_SUGGESTIONS} value={r.label}
                          onInputChange={(_, v) => updateSpec(i, 'label', v)}
                          renderInput={(p2) => (
                            <TextField {...p2} placeholder="Specifikimi — CPU, Targat, Licenca…" />
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField size="small" fullWidth value={r.value || ''}
                          placeholder="Vlera" onChange={(e) => updateSpec(i, 'value', e.target.value)} />
                      </TableCell>
                      <TableCell align="right" sx={{ width: 40, pr: 0 }}>
                        <IconButton size="small" color="error"
                          onClick={() => setSpecs((rows) => rows.filter((_, idx) => idx !== i))}>
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.75 }}>
                Sugjerimet ndjekin kategorinë; rreshtat pa vlerë nuk ruhen.
              </Typography>
            </Section>

            {/* ── Aksesorët ── */}
            <Section title={`Aksesorë${accessories.length ? ` · ${accessories.length}` : ''}`}
              icon={<AddIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
              action={
                <Button size="small" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                  onClick={() => setAccessories((r) => [...r, emptyAccessory()])}>
                  Shto aksesor
                </Button>
              }>
              {accessories.length === 0 ? (
                <Typography variant="body2" color="text.disabled">
                  Pa aksesorë. Shtoji si rreshta — te kthimi secili shënjohet veç.
                </Typography>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: 150, pl: 0 }}>Emri</TableCell>
                        <TableCell sx={{ minWidth: 150 }}>Përshkrimi</TableCell>
                        <TableCell sx={{ minWidth: 120 }}>S/N</TableCell>
                        <TableCell sx={{ minWidth: 130 }}>Gjendja</TableCell>
                        <TableCell sx={{ width: 40, pr: 0 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {accessories.map((a, i) => (
                        <TableRow key={a.uid}>
                          <TableCell sx={{ pl: 0 }}>
                            <TextField size="small" fullWidth value={a.name} placeholder="Ngarkues"
                              onChange={(e) => updateAccessory(i, 'name', e.target.value)} />
                          </TableCell>
                          <TableCell>
                            <TextField size="small" fullWidth value={a.description || ''}
                              onChange={(e) => updateAccessory(i, 'description', e.target.value)} />
                          </TableCell>
                          <TableCell>
                            <TextField size="small" fullWidth value={a.serialNumber || ''}
                              onChange={(e) => updateAccessory(i, 'serialNumber', e.target.value)} />
                          </TableCell>
                          <TableCell>
                            <TextField select size="small" fullWidth value={a.condition || 'good'}
                              onChange={(e) => updateAccessory(i, 'condition', e.target.value)}>
                              {CONDITIONS.map((c) => <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>)}
                            </TextField>
                          </TableCell>
                          <TableCell align="right" sx={{ pr: 0 }}>
                            <IconButton size="small" color="error"
                              onClick={() => setAccessories((r) => r.filter((_, idx) => idx !== i))}>
                              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Section>
          </Stack>
        </Grid>

        {/* ── Shiriti djathtas: rri ngjitur, që të mos lërë një brez bosh ── */}
        <Grid item xs={12} lg={4}>
          <Stack spacing={1.25} sx={{ position: { lg: 'sticky' }, top: { lg: 64 } }}>
            <Section title={`Foto të asetit${photos.length + pending.length ? ` · ${photos.length + pending.length}` : ''}`}
              icon={<UploadFileIcon sx={{ fontSize: 16, color: 'primary.main' }} />}>
              <Button size="small" variant="outlined" component="label" fullWidth
                startIcon={<UploadFileIcon sx={{ fontSize: 16 }} />}
                sx={{ textTransform: 'none', borderStyle: 'dashed', py: 0.75 }}>
                Kliko për të ngarkuar
                <input type="file" hidden accept="image/*" onChange={uploadPhoto} />
              </Button>
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                PNG, JPG, WEBP · maks. 10MB
                {!editing && pending.length > 0 && ' — ngarkohen me ruajtjen'}
              </Typography>
              {(photos.length > 0 || pending.length > 0) && (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  {photos.map((f) => (
                    <Box key={f.id} sx={{ position: 'relative' }}>
                      <AuthImage fileId={f.id} alt={f.name} sx={{ width: 92, height: 70 }} />
                      <IconButton size="small" onClick={() => removePhoto(f)}
                        sx={{ position: 'absolute', top: 1, right: 1, p: 0.25, bgcolor: 'background.paper' }}>
                        <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  ))}
                  {pending.map((x, i) => (
                    <Box key={x.url} sx={{ position: 'relative' }}>
                      <Box component="img" src={x.url} alt=""
                        sx={{ width: 92, height: 70, objectFit: 'cover', borderRadius: 2,
                              display: 'block', opacity: 0.75 }} />
                      <IconButton size="small" onClick={() => removePending(i)}
                        sx={{ position: 'absolute', top: 1, right: 1, p: 0.25, bgcolor: 'background.paper' }}>
                        <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              )}
            </Section>

            {!editing && (
              <Section title="Caktimi i parë"
                icon={<Inventory2Icon sx={{ fontSize: 16, color: 'primary.main' }} />}
                action={<Switch size="small" checked={assignOn} onChange={(e) => setAssignOn(e.target.checked)} />}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Cakto menjëherë te punonjësi. Regjistrohet si dorëzim i veçantë, ndaj hyn në historik.
                </Typography>
                {assignOn && (
                  <Stack spacing={1.25} sx={{ mt: 1.25 }}>
                    <WorkerPicker workers={workers} value={firstWorker} onChange={setFirstWorker}
                      label="Punonjësi" required />
                    <Stack direction="row" spacing={1}>
                      <TextField type="date" label="Data e dorëzimit" size="small" fullWidth {...SHRINK}
                        value={firstAssign.assignedOn}
                        onChange={(e) => setFirstAssign((f) => ({ ...f, assignedOn: e.target.value }))} />
                      <TextField select label="Gjendja" size="small" fullWidth {...SHRINK}
                        value={firstAssign.condition}
                        onChange={(e) => setFirstAssign((f) => ({ ...f, condition: e.target.value }))}>
                        {CONDITIONS.map((c) => <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>)}
                      </TextField>
                    </Stack>
                    <TextField label="Pajisjet shtesë" size="small" fullWidth {...SHRINK}
                      value={firstAssign.accessories} placeholder="Karikues, çantë…"
                      onChange={(e) => setFirstAssign((f) => ({ ...f, accessories: e.target.value }))} />
                    <TextField label="Komente" size="small" fullWidth multiline minRows={2} {...SHRINK}
                      value={firstAssign.note}
                      onChange={(e) => setFirstAssign((f) => ({ ...f, note: e.target.value }))} />
                    <Divider />
                    <Typography variant="caption" color="text.disabled">
                      Për procesverbal me nënshkrime dhe disa asete njëherësh, përdor
                      <strong> Dorëzim – Pranim i Asetit</strong>.
                    </Typography>
                  </Stack>
                )}
              </Section>
            )}
          </Stack>
        </Grid>
      </Grid>

      <Toast open={snack.open} message={snack.msg} severity={snack.sev}
        onClose={() => setSnack((s) => ({ ...s, open: false }))} />
    </ReportPage>
  );
}
